import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, Printer, AlertCircle } from "lucide-react";
import TrafficReportView from "../components/TrafficReportView";
import useSEO from "../lib/useSEO";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, no-auth, read-only traffic report. Lives at `/r/:token` so the URL
 * is short enough to drop into a text message. Sent by the admin to attorneys,
 * investors, banks etc. as live proof of platform traction.
 *
 * SEO: `noindex` because each token is private to the admin who minted it.
 */
export default function PublicTrafficReportPage() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, data: null, error: null });

    useSEO({
        title: "Site Traffic Report — LocalLawFinder",
        description: "Live site traffic snapshot for LocalLawFinder.",
        path: `/r/${token}`,
        noindex: true,
    });

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND}/api/public/traffic-report/${token}`);
                if (alive) setState({ loading: false, data, error: null });
            } catch (e) {
                if (alive) setState({
                    loading: false, data: null,
                    error: e?.response?.status === 404
                        ? "This report link has expired or been revoked."
                        : "Couldn't load the report. Try again in a moment.",
                });
            }
        })();
        return () => { alive = false; };
    }, [token]);

    return (
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="public-traffic-report">
            <header className="mb-8 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="ll-label mb-1 flex items-center gap-1.5">
                        <ShieldCheck className="h-3.5 w-3.5" /> Live traffic snapshot
                    </div>
                    <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight" data-testid="report-title">
                        {state.data?.label || "LocalLawFinder traffic"}
                    </h1>
                    {state.data && (
                        <p className="text-xs text-muted-foreground mt-2">
                            Snapshot generated {new Date(state.data.created_at).toLocaleDateString(undefined, { dateStyle: "long" })}
                            {" · "}numbers update in real time
                        </p>
                    )}
                </div>
                {state.data && (
                    <button onClick={() => window.print()}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-border hover:bg-accent print:hidden"
                            data-testid="report-print-btn">
                        <Printer className="h-3.5 w-3.5" /> Save as PDF
                    </button>
                )}
            </header>

            {state.loading && (
                <div className="flex items-center justify-center py-20" data-testid="report-loading">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            )}

            {state.error && (
                <div className="ll-card flex items-start gap-3 max-w-xl" data-testid="report-error">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div>
                        <h2 className="font-serif text-lg font-semibold mb-1">Report unavailable</h2>
                        <p className="text-sm text-muted-foreground">{state.error}</p>
                    </div>
                </div>
            )}

            {state.data && <TrafficReportView data={state.data.payload} readOnly />}

            {state.data && (
                <footer className="mt-10 pt-5 border-t border-border text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-2 print:text-[10px]">
                    <span>
                        Powered by <strong className="text-foreground">LocalLawFinder</strong> — a product of Young Black Man Inc.
                    </span>
                    <span>locallawfinder.com</span>
                </footer>
            )}
        </main>
    );
}
